import { useEffect, useRef, useState, type ReactNode } from 'react';
import * as RadixTabs from '@radix-ui/react-tabs';
import { cn } from '../../lib/cn';

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

export function Tabs({ items, defaultValue, value, onValueChange, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  // A tab strip wider than the viewport used to be clipped dead at the edge:
  // at 390px "Expenses (3)" was cut mid-word with no scrollbar, no fade and no
  // hint that a whole tab existed past the fold. The strip now scrolls, and the
  // fade is painted ONLY when something is actually hidden — drawn
  // unconditionally it dims the last tab of a strip that fits, which reads as a
  // clipped label rather than as "there is more". (Same rule as
  // SegmentedControl; keep the two in step.)
  const [hasMore, setHasMore] = useState(false);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const update = () => setHasMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 2);
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [items.length]);

  return (
    <RadixTabs.Root
      defaultValue={defaultValue ?? items[0]?.value}
      value={value}
      onValueChange={onValueChange}
      className={className}
    >
      <div className="relative">
        <RadixTabs.List
          ref={listRef}
          className="no-scrollbar flex gap-1 overflow-x-auto border-b border-grey-200"
          aria-label="Tabs"
        >
          {items.map((item) => (
            <RadixTabs.Trigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className={cn(
                'relative -mb-px shrink-0 whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm font-semibold text-grey-500',
                'transition-colors hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded-t-lg',
                'data-[state=active]:border-secondary data-[state=active]:text-secondary',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {item.label}
            </RadixTabs.Trigger>
          ))}
        </RadixTabs.List>
        {hasMore && (
          <span
            aria-hidden
            // grey-50 is the page ground these strips sit on — a white fade
            // would read as a pale block, not as a fade.
            className="pointer-events-none absolute bottom-px right-0 top-0 w-8 bg-gradient-to-l from-grey-50 via-grey-50/85 to-transparent"
          />
        )}
      </div>
      {items.map((item) => (
        <RadixTabs.Content
          key={item.value}
          value={item.value}
          className="pt-4 focus-visible:outline-none"
        >
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
